/**
 * Chat message contract — keep in sync with
 * flowops/src/lib/chat/message-contract.ts (FlowOps is source of truth).
 *
 * Two streams (do not mix):
 *   public  → user-visible transcript (SSE text-delta, Memory user/assistant, UI)
 *   private → orchestration only (seeds, ledgers, HITL control, plan handoffs)
 *
 * Primary gates are on the server (turnKind + Memory). This module is the
 * shared client sanitizer for restore, localStorage, and last-resort UI.
 *
 * Prefer typing new internal content as a private SSE event / turnKind —
 * do not grow regex forever for new agent prose.
 */

export type ChatVisibility = "public" | "private";

export type PrivateReason = "steer" | "plan_handoff" | "intent_control" | "ledger";

export type ChatRole = "user" | "assistant" | "system" | "tool" | "status";

export type PublicChatMessage = {
  id: string;
  type: string;
  text: string;
  createdAt?: string;
  details?: unknown;
};

const PRIVATE_BLOCK_RE = /<(ledger|working-memory)>[\s\S]*?<\/\1>/gi;
const PRIVATE_CLOSE_RE = /<\/(ledger|working-memory)>/i;
const PRIVATE_OPEN_RE = /<(ledger|working-memory)>/i;
const LEDGER_BLOCK_RE = /<ledger>([\s\S]*?)<\/ledger>/gi;
const OPEN_LEDGER_TAIL_RE = /<ledger>([\s\S]*)$/i;

/** Complete `<ledger>` bodies for Working notes cards (FlowOps). */
export function extractLedgerBodies(text: string): string[] {
  const bodies: string[] = [];
  const re = new RegExp(LEDGER_BLOCK_RE.source, LEDGER_BLOCK_RE.flags);
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const body = match[1]?.trim();
    if (body) bodies.push(body);
  }
  return bodies;
}

/** In-progress open `<ledger>` tail while the agent is still streaming notes. */
export function extractOpenLedgerTail(text: string): string | undefined {
  const withoutComplete = text.replace(LEDGER_BLOCK_RE, "");
  const open = withoutComplete.match(OPEN_LEDGER_TAIL_RE);
  const body = open?.[1]?.trim();
  return body || undefined;
}

/** Strip ledger / working-memory markup; keep surrounding public prose. */
export function stripLedgerMarkup(text: string): string {
  let visible = text.replace(PRIVATE_BLOCK_RE, "");
  const orphan = visible.match(PRIVATE_CLOSE_RE);
  if (orphan?.index != null) {
    visible = visible.slice(orphan.index + orphan[0].length);
  }
  const incompleteStart = visible.search(PRIVATE_OPEN_RE);
  if (incompleteStart >= 0) visible = visible.slice(0, incompleteStart);
  return visible.trim();
}

/** True when text is agent steering / resume seed — never a user bubble. */
export function isPrivateSteerText(text: string): boolean {
  const t = text.trim();
  if (!t) return false;

  if (/^The user (approved|rejected) Composio tool\b/i.test(t)) return true;
  if (/^The user finished responding to Composio\b/i.test(t)) return true;
  if (/^The user answered your question\b/i.test(t)) return true;
  if (/^The user submitted runtime inputs\b/i.test(t)) return true;
  if (/^Runtime inputs were submitted by the user\b/i.test(t)) return true;
  if (/^The previous approval UI expired\b/i.test(t)) return true;
  if (/^Continue autonomously\b/i.test(t)) return true;
  if (/^Continue from this session\./i.test(t)) return true;
  if (/^Use the user's answer\.\s*Call get_session_status\b/i.test(t)) return true;
  if (/^The user edited earlier answers\b/i.test(t)) return true;
  if (/^Plan review second pass\b/i.test(t)) return true;
  if (/^Planning is incomplete\b/i.test(t)) return true;
  if (/^Intent capture is incomplete\b/i.test(t)) return true;
  if (/^Draft build\b/i.test(t)) return true;
  if (/^Autonomous continuation\b/i.test(t)) return true;

  if (
    /\bexecute_composio_tool\b/i.test(t) &&
    /\b(grant skips a second card|Do not ask for approval again)\b/i.test(t)
  ) {
    return true;
  }

  if (
    /\bget_session_status\b/i.test(t) &&
    /\brecommendedNext\b/i.test(t) &&
    /\b(finalize_workflow|submit_plan)\b/i.test(t)
  ) {
    return true;
  }

  return false;
}

/** Plan → builder handoff seed (continue route), not chat. */
export function isPrivatePlanHandoffText(text: string): boolean {
  const t = text.trim();
  if (!t) return false;
  if (/Build the approved plan now\./i.test(t)) return true;
  if (/^#\s*(Approved|Pending) workflow plan\b/im.test(t) && /##\s*Build instructions\b/i.test(t)) {
    return true;
  }
  return false;
}

/** confirm-intent / planner control dumps leaked into the transcript. */
export function isPrivateIntentControlText(text: string): boolean {
  const t = text.trim();
  if (!t) return false;
  if (/The user CONFIRMED the structured business intent\b/i.test(t)) return true;
  if (/Intent capture is DONE\b/i.test(t) && /\bsubmit_intent\b/i.test(t)) {
    return true;
  }
  if (/\bdo NOT call submit_intent again\b/i.test(t) && /\bConfirmed intent\b/i.test(t)) {
    return true;
  }
  return false;
}

/**
 * Classify private orchestration text. Ledger markup is NOT whole-message private —
 * strip with stripLedgerMarkup / sanitizePublicTranscript instead.
 */
export function classifyPrivateText(text: string): PrivateReason | null {
  if (isPrivateSteerText(text)) return "steer";
  if (isPrivatePlanHandoffText(text)) return "plan_handoff";
  if (isPrivateIntentControlText(text)) return "intent_control";
  return null;
}

/** Central gate: should this text appear in the public chat transcript? */
export function isPublicChatText(role: ChatRole | string, text: string): boolean {
  if (role === "system" || role === "tool") return false;
  if (role === "status" || role === "error") return true;
  if (role === "user" || role === "assistant") {
    const stripped = stripLedgerMarkup(text);
    if (!stripped) return false;
    const reason = classifyPrivateText(stripped);
    if (reason === null) return true;
    // Steers are user/system seeds. Assistant copy that names those tools
    // (get_session_status, finalize_workflow, …) must stay visible.
    if (role === "assistant" && reason === "steer") return true;
    return false;
  }
  return true;
}

/**
 * Strip private orchestration from a transcript (Memory load, restore, UI).
 * Keeps plan/tool/status cards; promotes `<ledger>` bodies to Working notes rows;
 * strips ledger / working-memory tags from assistant prose.
 */
export function sanitizePublicTranscript<T extends PublicChatMessage>(messages: T[]): T[] {
  const out: T[] = [];
  let lastAssistantNorm = "";
  for (let index = 0; index < messages.length; index += 1) {
    const message = messages[index]!;
    if (message.type === "status" && isHiddenParkStatus(message.text)) continue;
    if (message.type === "user" && isLegacyComposioDecisionBubble(message)) continue;
    if (message.type === "ledger") {
      const text = message.text.trim();
      if (text) out.push(message);
      lastAssistantNorm = "";
      continue;
    }

    if (message.type === "user" || message.type === "assistant") {
      if (message.type === "assistant") {
        const ledgers = extractLedgerBodies(message.text);
        for (let ledgerIndex = 0; ledgerIndex < ledgers.length; ledgerIndex += 1) {
          out.push({
            ...message,
            id: `${message.id}:ledger:${ledgerIndex}`,
            type: "ledger",
            text: ledgers[ledgerIndex]!,
          });
        }
        const openTail = extractOpenLedgerTail(message.text);
        if (openTail) {
          out.push({
            ...message,
            id: `${message.id}:ledger:live`,
            type: "ledger",
            text: openTail,
            details: {
              ...(typeof message.details === "object" && message.details ? message.details : {}),
              live: true,
            },
          });
        }
      }

      let stripped = stripLedgerMarkup(message.text);
      if (!stripped) {
        if (message.type === "assistant") lastAssistantNorm = "";
        continue;
      }
      if (!isPublicChatText(message.type, stripped)) continue;
      if (message.type === "assistant") {
        stripped = collapseRepeatedAssistantBody(stripped);
        if (isThinAssistantFiller(stripped)) continue;
        if (isPostPlanReviewSpeech(stripped) && hasPrecedingPlanCard(messages, index)) {
          continue;
        }
        const norm = stripped.replace(/\s+/g, " ").trim().toLowerCase();
        if (norm && norm === lastAssistantNorm) continue;
        lastAssistantNorm = norm;
      } else {
        lastAssistantNorm = "";
      }
      out.push(stripped === message.text ? message : { ...message, text: stripped });
      continue;
    }
    lastAssistantNorm = "";
    out.push(message);
  }
  return out;
}

/** Exact doubled assistant body (Mastra turn replay into one bubble). */
export function collapseRepeatedAssistantBody(text: string): string {
  const trimmed = text.trim();
  if (trimmed.length < 40) return text;
  const parts = trimmed
    .split(/\n\n+/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length >= 2 && parts.length % 2 === 0) {
    const half = parts.length / 2;
    const left = parts.slice(0, half).join("\n\n");
    const right = parts.slice(half).join("\n\n");
    if (left.replace(/\s+/g, " ").toLowerCase() === right.replace(/\s+/g, " ").toLowerCase()) {
      return left;
    }
  }
  const mid = Math.floor(trimmed.length / 2);
  const left = trimmed.slice(0, mid).trim();
  const right = trimmed.slice(mid).trim();
  if (left && left === right) return left;
  return text;
}

/** LLM restates the plan after submit_plan — PlanReviewCard already shows plan.summary. */
export function isPostPlanReviewSpeech(text: string): boolean {
  return /^i[''\u2019]?ve (prepared|submitted) the (workflow )?plan for review\b/i.test(
    text.trim(),
  );
}

function hasPrecedingPlanCard(messages: PublicChatMessage[], index: number): boolean {
  for (let i = index - 1; i >= 0; i -= 1) {
    const item = messages[i];
    if (!item) continue;
    if (item.type === "plan") return true;
    if (item.type === "user" || item.type === "intent") return false;
  }
  return false;
}

/** HITL park copy — the card is the UI. */
export function isHiddenParkStatus(text: string): boolean {
  return /builder is|waiting for your (answer|input)|approval required|runtime inputs|input resolved/i.test(
    text,
  );
}

/** Legacy Composio approve/reject user bubbles — the settled card is the UI. */
export function isLegacyComposioDecisionBubble(message: PublicChatMessage): boolean {
  if (message.type !== "user") return false;
  const kind = (message.details as { kind?: string } | undefined)?.kind;
  if (kind === "composio-decision" || kind === "confirmed-answers") return false;
  return /^(Approved|Rejected)\s+[A-Z][A-Z0-9_]+\.?$/i.test(message.text.trim());
}

/** Park / soft-HITL filler — mirror of aunix-agents memory-transcript-hygiene. */
export function isThinAssistantFiller(text: string): boolean {
  const t = stripLedgerMarkup(text).trim();
  if (!t) return true;
  // Do not drop short real replies ("Got it.", "Done.") — only park/waiting patterns.
  if (/blocked on the plan\b/i.test(t)) return true;
  if (/waiting for (your |the )?(answer|approval|input)\b/i.test(t)) return true;
  if (/waiting for (composio|plan|user|mastra)\b/i.test(t)) return true;
  if (/^suspended\.?$/i.test(t)) return true;
  if (/queued .{0,120}approval/i.test(t)) return true;
  if (/once (those|that|it|they) (are|is) approved/i.test(t)) return true;
  if (/approval card(s)? (is|are) (now )?in (the )?chat/i.test(t)) return true;
  return false;
}
